/**
 * pf-sections.js
 * Section- and block-specific behavior for the pf-1842 theme.
 * Add new section JS here, namespaced by the section element's tag/class.
 *
 * Loaded once from layout/theme.liquid with `defer`, AFTER swiper-bundle.min.js.
 * Defer preserves source order, so by the time custom elements registered
 * here upgrade (and `connectedCallback` runs), `window.Swiper` is defined.
 */

(function () {
  // ---------------------------------------------------------------------------
  // <pf-announcement-bar>
  // Thin top-of-page bar. With one slide, static. With two or more, Swiper
  // slider with optional autoplay, arrows, and per-section session-storage
  // dismiss.
  // ---------------------------------------------------------------------------
  if (!customElements.get('pf-announcement-bar')) {
    class PfAnnouncementBar extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.dismissible = this.dataset.dismissible === 'true';

        if (this.dismissible && this.isDismissed()) {
          this.hidden = true;
          return;
        }

        this.swiperEl = this.querySelector('.swiper');
        this.dismissButton = this.querySelector('.pf-announcement-bar__dismiss');
        this.prevButton = this.querySelector('.pf-announcement-bar__nav--prev');
        this.nextButton = this.querySelector('.pf-announcement-bar__nav--next');

        this.initSwiper();
        this.bindDismiss();
      }

      initSwiper() {
        if (!this.swiperEl) return;
        const slides = this.swiperEl.querySelectorAll('.swiper-slide');
        if (slides.length === 0) return;

        const autoplay = this.dataset.autoplay === 'true';
        const autoplayDelay = parseInt(this.dataset.autoplayDelay, 10) || 4000;
        const showArrows = this.dataset.showArrows === 'true';
        const multi = slides.length > 1;

        const config = {
          loop: multi,
          slidesPerView: 1,
          spaceBetween: 0,
          allowTouchMove: multi,
        };

        if (autoplay && multi) {
          config.autoplay = {
            delay: autoplayDelay,
            disableOnInteraction: false,
          };
        }

        if (showArrows && multi && this.prevButton && this.nextButton) {
          config.navigation = { prevEl: this.prevButton, nextEl: this.nextButton };
        }

        this.swiper = new window.Swiper(this.swiperEl, config);
      }

      bindDismiss() {
        if (!this.dismissButton) return;
        this.dismissButton.addEventListener('click', () => {
          this.markDismissed();
          this.hidden = true;
          if (this.swiper) {
            try { this.swiper.destroy(true, true); } catch (e) { /* noop */ }
          }
        });
      }

      storageKey() {
        return 'pf-announcement-bar-dismissed:' + (this.sectionId || 'default');
      }

      isDismissed() {
        try {
          return sessionStorage.getItem(this.storageKey()) === '1';
        } catch (e) {
          return false;
        }
      }

      markDismissed() {
        try {
          sessionStorage.setItem(this.storageKey(), '1');
        } catch (e) { /* storage might be blocked — fail silently */ }
      }
    }

    customElements.define('pf-announcement-bar', PfAnnouncementBar);
  }

  // ---------------------------------------------------------------------------
  // <pf-homepage-hero>
  // Single-slide static banner OR multi-slide Swiper hero (loop, fade/slide
  // transition, optional autoplay/arrows/pagination).
  // ---------------------------------------------------------------------------
  if (!customElements.get('pf-homepage-hero')) {
    class PfHomepageHero extends HTMLElement {
      connectedCallback() {
        this.swiperEl = this.querySelector('.swiper');
        this.prevButton = this.querySelector('.pf-homepage-hero__nav--prev');
        this.nextButton = this.querySelector('.pf-homepage-hero__nav--next');
        this.paginationEl = this.querySelector('.pf-homepage-hero__pagination');

        this.initSwiper();
      }

      initSwiper() {
        if (!this.swiperEl) return;
        const slides = this.swiperEl.querySelectorAll('.swiper-slide');
        if (slides.length <= 1) return;

        const autoplay = this.dataset.autoplay === 'true';
        const autoplayDelay = parseInt(this.dataset.autoplayDelay, 10) || 5000;
        const showArrows = this.dataset.showArrows === 'true';
        const showPagination = this.dataset.showPagination === 'true';
        const transition = this.dataset.transition || 'fade';

        const config = {
          loop: true,
          slidesPerView: 1,
          spaceBetween: 0,
          speed: 700,
        };

        if (transition === 'fade') {
          config.effect = 'fade';
          config.fadeEffect = { crossFade: true };
        }

        if (autoplay) {
          config.autoplay = {
            delay: autoplayDelay,
            disableOnInteraction: false,
          };
        }

        if (showArrows && this.prevButton && this.nextButton) {
          config.navigation = { prevEl: this.prevButton, nextEl: this.nextButton };
        }

        if (showPagination && this.paginationEl) {
          config.pagination = {
            el: this.paginationEl,
            clickable: true,
          };
        }

        this.swiper = new window.Swiper(this.swiperEl, config);
      }
    }

    customElements.define('pf-homepage-hero', PfHomepageHero);
  }

  // ---------------------------------------------------------------------------
  // <pf-collection-products>
  // Reusable product-listing section. Slider mode uses Swiper with
  // `slidesPerView: 'auto'` (slide widths set via CSS variable so cards
  // auto-fit and the rightmost card can peek off the viewport edge to
  // signal scrollability). Grid mode is plain CSS Grid (no JS layout).
  // Swatch clicks swap the card's primary image to the variant's image.
  // ---------------------------------------------------------------------------
  if (!customElements.get('pf-collection-products')) {
    class PfCollectionProducts extends HTMLElement {
      connectedCallback() {
        this.layout = this.dataset.layout;
        this.swiperEl = this.querySelector('.swiper');
        this.prevButton = this.querySelector('.pf-collection-products__nav--prev');
        this.nextButton = this.querySelector('.pf-collection-products__nav--next');

        if (this.layout === 'slider') {
          this.initSwiper();
        }

        this.bindSwatches();
      }

      initSwiper() {
        if (!this.swiperEl) return;
        const slides = this.swiperEl.querySelectorAll('.swiper-slide');
        if (slides.length === 0) return;

        const gapDesktop = parseInt(this.dataset.gapDesktop, 10) || 16;
        const gapMobile = parseInt(this.dataset.gapMobile, 10) || 8;
        const spvDesktop = parseFloat(this.dataset.slidesPerViewDesktop) || 4.2;
        const spvTablet = parseFloat(this.dataset.slidesPerViewTablet) || 3.2;
        const spvMobile = parseFloat(this.dataset.slidesPerViewMobile) || 2.2;

        this.swiper = new window.Swiper(this.swiperEl, {
          // Mobile defaults (overridden by breakpoints below as the viewport grows).
          slidesPerView: spvMobile,
          spaceBetween: gapMobile,
          breakpoints: {
            750: { slidesPerView: spvTablet, spaceBetween: gapDesktop },
            990: { slidesPerView: spvDesktop, spaceBetween: gapDesktop },
          },
          navigation: this.prevButton && this.nextButton
            ? { prevEl: this.prevButton, nextEl: this.nextButton }
            : false,
          watchOverflow: true,
        });
      }

      bindSwatches() {
        const cards = this.querySelectorAll('.pf-product-card');
        cards.forEach((card) => {
          const swatches = card.querySelectorAll('.pf-product-card__swatch');
          if (swatches.length === 0) return;

          const primary = card.querySelector('.pf-product-card__image--primary');
          if (!primary) return;

          const originalSrc = primary.getAttribute('src');
          const originalSrcset = primary.getAttribute('srcset');

          swatches.forEach((swatch) => {
            swatch.addEventListener('click', (event) => {
              event.preventDefault();
              event.stopPropagation();

              const newSrc = swatch.dataset.imageSrc;
              const newSrcset = swatch.dataset.imageSrcset;
              if (!newSrc) return;

              if (newSrcset) primary.setAttribute('srcset', newSrcset);
              primary.setAttribute('src', newSrc);

              swatches.forEach((s) => s.removeAttribute('aria-pressed'));
              swatch.setAttribute('aria-pressed', 'true');
            });
          });

          // Restore original image when leaving the card so hover-secondary
          // resets cleanly. (Click selection persists; this just guards the
          // edge case of cursor moving away mid-interaction.)
          card.addEventListener('mouseleave', () => {
            const activeSwatch = card.querySelector('.pf-product-card__swatch[aria-pressed="true"]');
            if (!activeSwatch) {
              primary.setAttribute('src', originalSrc);
              if (originalSrcset) primary.setAttribute('srcset', originalSrcset);
            }
          });
        });
      }
    }

    customElements.define('pf-collection-products', PfCollectionProducts);
  }

  // ---------------------------------------------------------------------------
  // <pf-collection-list>
  // Reusable collection-listing section. Same slider/grid duality as
  // <pf-collection-products>. Slider mode wires Swiper with explicit
  // slidesPerView per breakpoint (desktop / tablet / mobile, decimal-friendly).
  // Grid mode is plain CSS Grid with no JS layout.
  // ---------------------------------------------------------------------------
  if (!customElements.get('pf-collection-list')) {
    class PfCollectionList extends HTMLElement {
      connectedCallback() {
        this.layout = this.dataset.layout;
        this.swiperEl = this.querySelector('.swiper');
        this.prevButton = this.querySelector('.pf-collection-list__nav--prev');
        this.nextButton = this.querySelector('.pf-collection-list__nav--next');

        if (this.layout === 'slider') {
          this.initSwiper();
        }
      }

      initSwiper() {
        if (!this.swiperEl) return;
        const slides = this.swiperEl.querySelectorAll('.swiper-slide');
        if (slides.length === 0) return;

        const gapDesktop = parseInt(this.dataset.gapDesktop, 10) || 16;
        const gapMobile = parseInt(this.dataset.gapMobile, 10) || 8;
        const spvDesktop = parseFloat(this.dataset.slidesPerViewDesktop) || 4.2;
        const spvTablet = parseFloat(this.dataset.slidesPerViewTablet) || 3.2;
        const spvMobile = parseFloat(this.dataset.slidesPerViewMobile) || 2.2;

        this.swiper = new window.Swiper(this.swiperEl, {
          slidesPerView: spvMobile,
          spaceBetween: gapMobile,
          breakpoints: {
            750: { slidesPerView: spvTablet, spaceBetween: gapDesktop },
            990: { slidesPerView: spvDesktop, spaceBetween: gapDesktop },
          },
          navigation: this.prevButton && this.nextButton
            ? { prevEl: this.prevButton, nextEl: this.nextButton }
            : false,
          watchOverflow: true,
        });
      }
    }

    customElements.define('pf-collection-list', PfCollectionList);
  }

  // ---------------------------------------------------------------------------
  // <pf-media-with-text>
  // No layout JS — section is fully declarative. Only purpose: nudge the
  // <video autoplay> element into actually playing. Browser autoplay policies
  // sometimes refuse the initial autoplay even with `muted` + `playsinline`
  // (especially with HLS sources or low-data mode). We call .play() once when
  // the element connects and again when it scrolls into view; both calls have
  // their rejections swallowed silently.
  // ---------------------------------------------------------------------------
  if (!customElements.get('pf-media-with-text')) {
    class PfMediaWithText extends HTMLElement {
      connectedCallback() {
        const video = this.querySelector('video[autoplay]');
        if (!video) return;

        const tryPlay = () => {
          const result = video.play();
          if (result && typeof result.catch === 'function') {
            result.catch(() => { /* autoplay blocked — ignore */ });
          }
        };

        // First attempt right away.
        tryPlay();

        // Fallback: retry when the video scrolls into view (covers cases where
        // the page started in a background tab or with reduced data).
        if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && video.paused) tryPlay();
            });
          }, { threshold: 0.25 });
          observer.observe(video);
        }
      }
    }

    customElements.define('pf-media-with-text', PfMediaWithText);
  }

  // ---------------------------------------------------------------------------
  // <pf-usp-bar>
  // USP/promise bar with icon-text cards. Desktop is always a grid (handled
  // entirely in CSS via gap-as-border on a tinted row bg). On tablet/mobile
  // the section either stacks (grid mode) or becomes a CSS scroll-snap slider
  // (slider mode) — we never load Swiper for this section. This class only
  // wires the slider's pagination dots: clicks scroll to the matching card,
  // and the active dot stays in sync with the row's scroll position.
  // ---------------------------------------------------------------------------
  if (!customElements.get('pf-usp-bar')) {
    class PfUspBar extends HTMLElement {
      connectedCallback() {
        this.layout = this.dataset.mobileLayout;
        if (this.layout !== 'slider') return;

        this.row = this.querySelector('.pf-usp-bar__row');
        this.dots = Array.from(this.querySelectorAll('.pf-usp-bar__dot'));
        this.cards = Array.from(this.querySelectorAll('.pf-usp-card'));
        if (!this.row || this.dots.length === 0 || this.cards.length === 0) return;

        this.bindDots();
        this.bindScroll();
        this.updateActiveDot();
      }

      bindDots() {
        this.dots.forEach((dot, i) => {
          dot.addEventListener('click', () => {
            const card = this.cards[i];
            if (!card) return;
            // scrollIntoView with inline:'start' aligns the card's left edge to
            // the row's scroll origin, which combined with scroll-snap-align:
            // start gives a clean snap to the chosen slide.
            card.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
          });
        });
      }

      bindScroll() {
        let frame = null;
        this.row.addEventListener(
          'scroll',
          () => {
            if (frame) cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => this.updateActiveDot());
          },
          { passive: true }
        );
      }

      updateActiveDot() {
        const rowRect = this.row.getBoundingClientRect();
        let activeIndex = 0;
        let bestDiff = Infinity;
        this.cards.forEach((card, i) => {
          const cardRect = card.getBoundingClientRect();
          const diff = Math.abs(cardRect.left - rowRect.left);
          if (diff < bestDiff) {
            bestDiff = diff;
            activeIndex = i;
          }
        });
        this.dots.forEach((dot, i) => {
          dot.setAttribute('aria-current', i === activeIndex ? 'true' : 'false');
        });
      }
    }

    customElements.define('pf-usp-bar', PfUspBar);
  }

  // ---------------------------------------------------------------------------
  // <pf-footer>
  // Mobile-only accordion animation for `.pf-footer__column--collapsible`
  // menu columns. Server renders `<button aria-expanded="true">` + content
  // — pre-JS users see everything expanded (no collapse, but full access).
  // On JS upgrade, mobile viewports flip aria-expanded to "false" to start
  // collapsed; CSS hides content via `height: 0; overflow: hidden`. On
  // summary click, JS toggles aria-expanded and animates inline `height`
  // between 0 and scrollHeight. Desktop (≥750px) ignores clicks entirely
  // (CSS pointer-events:none) and never collapses (aria-expanded stays
  // "true"). A matchMedia listener handles viewport flips during editing.
  // ---------------------------------------------------------------------------
  if (!customElements.get('pf-footer')) {
    class PfFooter extends HTMLElement {
      connectedCallback() {
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.accordions = Array.from(
          this.querySelectorAll('.pf-footer__column--collapsible .pf-footer__accordion')
        );
        if (this.accordions.length === 0) return;

        this.accordions.forEach((accordion) => {
          const summary = accordion.querySelector('.pf-footer__summary');
          const content = accordion.querySelector('.pf-footer__column-content');
          if (!summary || !content) return;

          // Start collapsed on mobile. Desktop keeps the server's "true".
          if (this.mobileQuery.matches) {
            summary.setAttribute('aria-expanded', 'false');
          }

          summary.addEventListener('click', (e) => this.handleClick(e, summary, content));
        });

        // React to viewport changes (e.g. theme editor resize / device rotation).
        // matchMedia.addEventListener is the modern listener API; supported in
        // all evergreen browsers we target.
        this.mobileQuery.addEventListener('change', (e) => this.handleViewportChange(e));
      }

      handleClick(event, summary, content) {
        // Desktop summary is pointer-events:none in CSS; this is a defensive
        // guard in case the breakpoint changes between paint and click.
        if (!this.mobileQuery.matches) return;
        event.preventDefault();

        if (content.dataset.animating === 'true') return;
        content.dataset.animating = 'true';

        const wasExpanded = summary.getAttribute('aria-expanded') === 'true';

        const onEnd = (e) => {
          // Match strictly on this content + the `height` property so unrelated
          // descendant transitions don't trigger cleanup.
          if (e.target !== content || e.propertyName !== 'height') return;
          content.removeEventListener('transitionend', onEnd);
          content.style.height = '';
          content.style.overflow = '';
          content.dataset.animating = 'false';
        };
        content.addEventListener('transitionend', onEnd);
        content.style.overflow = 'hidden';

        if (wasExpanded) {
          // Closing: lock current height, then drop to 0 next frame. Flip
          // aria-expanded simultaneously so the chevron rotation runs alongside.
          const startHeight = content.scrollHeight;
          content.style.height = startHeight + 'px';
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              summary.setAttribute('aria-expanded', 'false');
              content.style.height = '0px';
            });
          });
        } else {
          // Opening: flip aria-expanded so CSS reveals padding-top, then measure
          // the new natural height and animate from 0 → there.
          summary.setAttribute('aria-expanded', 'true');
          const targetHeight = content.scrollHeight;
          content.style.height = '0px';
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              content.style.height = targetHeight + 'px';
            });
          });
        }
      }

      handleViewportChange(event) {
        // Switched into mobile: collapse all and clear any leftover inline
        // heights. Switched into desktop: expand all and clear inline heights
        // so the natural layout reasserts.
        const expanded = event.matches ? 'false' : 'true';
        this.accordions.forEach((accordion) => {
          const summary = accordion.querySelector('.pf-footer__summary');
          const content = accordion.querySelector('.pf-footer__column-content');
          if (!summary || !content) return;
          summary.setAttribute('aria-expanded', expanded);
          content.style.height = '';
          content.style.overflow = '';
          content.dataset.animating = 'false';
        });
      }
    }

    customElements.define('pf-footer', PfFooter);
  }

  // ---------------------------------------------------------------------------
  // <pf-faq>
  // ARIA accordion. Server renders every question with aria-expanded="false"
  // and every answer at height: 0 (base CSS). CSS rule
  //   .pf-faq .pf-faq__item.is-open .pf-faq__answer { height: auto }
  // owns the open RESTING state — the JS only sets inline `height` during
  // a transition (start → end), then clears it so the CSS rule takes over.
  //
  // Two modes, chosen via the host's data-animate attribute:
  //   data-animate="true"  → CSS rule applies `transition: height …`. JS
  //                          pins an explicit px start, forces a reflow, then
  //                          sets the px end so the browser fires the
  //                          transition. transitionend hands back to CSS.
  //   data-animate="false" → No CSS transition. JS skips the px dance and
  //                          just toggles `is-open`. State change is instant
  //                          and no `dataset.animating` flag is set, so
  //                          subsequent clicks aren't blocked.
  //
  // All event listeners are attached with { signal } from a single
  // AbortController so disconnectedCallback can tear them all down at once,
  // including any in-flight transitionend handlers.
  // ---------------------------------------------------------------------------
  if (!customElements.get('pf-faq')) {
    class PfFaq extends HTMLElement {
      connectedCallback() {
        this.allowMultiple = this.dataset.allowMultiple === 'true';
        this.firstOpen = this.dataset.firstOpen === 'true';
        this.animate = this.dataset.animate === 'true';
        this.items = Array.from(this.querySelectorAll('.pf-faq__item'));
        if (this.items.length === 0) return;

        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.items.forEach((item, i) => {
          const button = item.querySelector('.pf-faq__question');
          const answer = item.querySelector('.pf-faq__answer');
          if (!button || !answer) return;

          button.addEventListener('click', () => this.toggle(item), { signal });

          if (i === 0 && this.firstOpen) {
            this.setOpenState(item, true, { instant: true });
          }
        });
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      toggle(item) {
        if (item.dataset.animating === 'true') return;

        const open = !item.classList.contains('is-open');

        if (open && !this.allowMultiple) {
          this.items.forEach((other) => {
            if (other !== item && other.classList.contains('is-open')) {
              this.setOpenState(other, false);
            }
          });
        }

        this.setOpenState(item, open);
      }

      setOpenState(item, open, { instant = false } = {}) {
        const button = item.querySelector('.pf-faq__question');
        const answer = item.querySelector('.pf-faq__answer');
        if (!button || !answer) return;

        button.setAttribute('aria-expanded', open ? 'true' : 'false');
        answer.setAttribute('aria-hidden', open ? 'false' : 'true');

        // Instant path — used for the first-open default and whenever the
        // section has animation disabled. No transition will fire, so don't
        // arm the animating flag or a transitionend listener.
        if (instant || !this.animate) {
          item.classList.toggle('is-open', open);
          answer.style.height = '';
          item.dataset.animating = 'false';
          return;
        }

        item.dataset.animating = 'true';

        if (open) {
          // Pin at 0 first so the inline value overrides any CSS state,
          // then add `is-open` (CSS would jump to height: auto, but inline 0
          // wins). Force a reflow with `offsetHeight` to commit the 0px
          // start frame — without this, the next inline change in the same
          // task is coalesced by the browser and no transition fires.
          answer.style.height = '0px';
          item.classList.add('is-open');
          void answer.offsetHeight;
          answer.style.height = answer.scrollHeight + 'px';
        } else {
          // Snapshot the natural open height while is-open is still applied
          // (so padding-top is included), pin the answer at that height,
          // remove is-open, force reflow, then animate down to 0.
          answer.style.height = answer.scrollHeight + 'px';
          item.classList.remove('is-open');
          void answer.offsetHeight;
          answer.style.height = '0px';
        }

        answer.addEventListener(
          'transitionend',
          (e) => {
            if (e.target !== answer || e.propertyName !== 'height') return;
            // Hand off to CSS — base rule (closed) or .is-open rule (open)
            // resolves the resting height now.
            answer.style.height = '';
            item.dataset.animating = 'false';
          },
          { once: true, signal: this.abortController.signal }
        );
      }
    }

    customElements.define('pf-faq', PfFaq);
  }

  // ---------------------------------------------------------------------------
  // <pf-header>
  // Hamburger → mobile drawer, locale popovers (language + currency), desktop
  // submenu dropdowns, and click-routing for the cart / search icons. All
  // listeners are attached with { signal } from a single AbortController so
  // disconnectedCallback tears everything down at once.
  //
  // Cart / search actions look for a registered global controller first
  // (window.pfCart / window.pfSearch). Those land in subsequent milestones —
  // until they do, clicking the buttons falls back to /cart and /search.
  // ---------------------------------------------------------------------------
  if (!customElements.get('pf-header')) {
    class PfHeader extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.mobileMenu = this.querySelector('[data-pf-mobile-menu]');
        this.bindMobileMenu(signal);
        this.bindSubmenus(signal);
        this.bindLocalePopovers(signal);
        this.bindActionButtons(signal);
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.unlockScroll();
      }

      // ---- Mobile menu drawer ----
      bindMobileMenu(signal) {
        const openBtn = this.querySelector('[data-pf-mobile-menu-open]');
        if (!openBtn || !this.mobileMenu) return;

        const backdrop = this.mobileMenu.querySelector('[data-pf-mobile-menu-backdrop]');
        const closers = this.mobileMenu.querySelectorAll('[data-pf-mobile-menu-close]');

        openBtn.addEventListener('click', () => this.openMobileMenu(), { signal });

        if (backdrop) {
          backdrop.addEventListener('click', () => this.closeMobileMenu(), { signal });
        }

        closers.forEach((el) => {
          // Anchors close the drawer on click but still navigate; we don't
          // preventDefault, just trigger the close + cleanup.
          el.addEventListener('click', () => this.closeMobileMenu(), { signal });
        });

        document.addEventListener(
          'keydown',
          (e) => {
            if (e.key === 'Escape' && this.mobileMenu.classList.contains('is-open')) {
              this.closeMobileMenu();
            }
          },
          { signal }
        );

        // Mobile submenu toggles inside the drawer.
        const subToggles = this.mobileMenu.querySelectorAll('[data-pf-mobile-submenu-toggle]');
        subToggles.forEach((btn) => {
          btn.addEventListener('click', () => this.toggleMobileSubmenu(btn), { signal });
        });
      }

      openMobileMenu() {
        if (!this.mobileMenu) return;
        this.mobileMenu.hidden = false;
        // Force a reflow so the initial transform/opacity (translateX(-100%) / 0)
        // is committed before we add the .is-open class that transitions them.
        void this.mobileMenu.offsetHeight;
        this.mobileMenu.classList.add('is-open');
        this.lockScroll();
        const openBtn = this.querySelector('[data-pf-mobile-menu-open]');
        if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
        // Move focus into the drawer (close button) for keyboard users.
        const closeBtn = this.mobileMenu.querySelector('.pf-header__mobile-menu-close');
        if (closeBtn) closeBtn.focus({ preventScroll: true });
      }

      closeMobileMenu() {
        if (!this.mobileMenu) return;
        if (!this.mobileMenu.classList.contains('is-open')) return;

        const panel = this.mobileMenu.querySelector('.pf-header__mobile-menu-panel');
        this.mobileMenu.classList.remove('is-open');
        this.unlockScroll();
        const openBtn = this.querySelector('[data-pf-mobile-menu-open]');
        if (openBtn) openBtn.setAttribute('aria-expanded', 'false');

        // Wait for the slide-out to complete before hiding entirely; if no
        // transition is defined (e.g. reduced motion), hide immediately.
        const onEnd = () => {
          this.mobileMenu.hidden = true;
          panel?.removeEventListener('transitionend', onEnd);
        };
        if (panel) {
          panel.addEventListener('transitionend', onEnd, { once: true, signal: this.abortController.signal });
          // Safety net — in case transitionend never fires (display:none on a
          // descendant, reduced motion, etc.) hide after the longest CSS duration.
          setTimeout(onEnd, 340);
        } else {
          this.mobileMenu.hidden = true;
        }
        if (openBtn) openBtn.focus({ preventScroll: true });
      }

      toggleMobileSubmenu(button) {
        const item = button.closest('.pf-header__mobile-menu-item');
        const panel = button.parentElement.querySelector('[data-pf-mobile-submenu]');
        if (!item || !panel) return;

        const isOpen = item.classList.contains('is-open');

        if (isOpen) {
          // Closing: pin current height, then drop to 0.
          panel.style.height = panel.scrollHeight + 'px';
          item.classList.remove('is-open');
          button.setAttribute('aria-expanded', 'false');
          panel.setAttribute('aria-hidden', 'true');
          void panel.offsetHeight;
          panel.style.height = '0px';
        } else {
          // Opening: from 0 → measured height. Add class first so CSS
          // applies; that gives height: auto, but inline 0 still wins.
          panel.style.height = '0px';
          item.classList.add('is-open');
          button.setAttribute('aria-expanded', 'true');
          panel.setAttribute('aria-hidden', 'false');
          void panel.offsetHeight;
          panel.style.height = panel.scrollHeight + 'px';
        }

        panel.addEventListener(
          'transitionend',
          (e) => {
            if (e.target !== panel || e.propertyName !== 'height') return;
            panel.style.height = '';
          },
          { once: true, signal: this.abortController.signal }
        );
      }

      lockScroll() {
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
      }

      unlockScroll() {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
      }

      // ---- Desktop submenu dropdowns ----
      bindSubmenus(signal) {
        const items = this.querySelectorAll('.pf-header__menu-item--has-children');
        items.forEach((item) => {
          const toggle = item.querySelector('[data-pf-submenu-toggle]');
          const panel = item.querySelector('[data-pf-submenu]');
          if (!toggle || !panel) return;

          const open = () => {
            panel.hidden = false;
            toggle.setAttribute('aria-expanded', 'true');
          };
          const close = () => {
            panel.hidden = true;
            toggle.setAttribute('aria-expanded', 'false');
          };

          // Hover + focus on the item opens; mouseleave closes.
          item.addEventListener('mouseenter', open, { signal });
          item.addEventListener('mouseleave', close, { signal });
          item.addEventListener('focusin', open, { signal });
          item.addEventListener('focusout', (e) => {
            // Only close if focus actually left the entire item subtree.
            if (!item.contains(e.relatedTarget)) close();
          }, { signal });

          // Click toggle for keyboard users and touch on desktop-sized screens.
          toggle.addEventListener('click', (e) => {
            e.preventDefault();
            const expanded = toggle.getAttribute('aria-expanded') === 'true';
            if (expanded) close();
            else open();
          }, { signal });
        });
      }

      // ---- Locale popovers (language + currency) ----
      bindLocalePopovers(signal) {
        const popovers = this.querySelectorAll('[data-pf-popover]');
        popovers.forEach((pop) => {
          const trigger = pop.querySelector('[data-pf-popover-trigger]');
          const panel = pop.querySelector('[data-pf-popover-panel]');
          if (!trigger || !panel) return;

          trigger.addEventListener('click', (e) => {
            e.preventDefault();
            const expanded = trigger.getAttribute('aria-expanded') === 'true';
            this.closeAllPopovers();
            if (!expanded) {
              trigger.setAttribute('aria-expanded', 'true');
              panel.hidden = false;
            }
          }, { signal });
        });

        // Click-outside closes all open popovers.
        document.addEventListener('click', (e) => {
          if (!this.contains(e.target)) {
            this.closeAllPopovers();
            return;
          }
          // Click landed inside the header — close any popover that doesn't
          // contain the click.
          this.querySelectorAll('[data-pf-popover]').forEach((pop) => {
            if (!pop.contains(e.target)) {
              const t = pop.querySelector('[data-pf-popover-trigger]');
              const p = pop.querySelector('[data-pf-popover-panel]');
              if (t && p && t.getAttribute('aria-expanded') === 'true') {
                t.setAttribute('aria-expanded', 'false');
                p.hidden = true;
              }
            }
          });
        }, { signal });

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this.closeAllPopovers();
        }, { signal });
      }

      closeAllPopovers() {
        this.querySelectorAll('[data-pf-popover]').forEach((pop) => {
          const t = pop.querySelector('[data-pf-popover-trigger]');
          const p = pop.querySelector('[data-pf-popover-panel]');
          if (t && p) {
            t.setAttribute('aria-expanded', 'false');
            p.hidden = true;
          }
        });
      }

      // ---- Cart / search action buttons ----
      bindActionButtons(signal) {
        const buttons = this.querySelectorAll('[data-pf-action]');
        buttons.forEach((btn) => {
          btn.addEventListener('click', (e) => {
            const action = btn.dataset.pfAction;
            if (action === 'search-open') {
              if (window.pfSearch && typeof window.pfSearch.open === 'function') {
                e.preventDefault();
                window.pfSearch.open();
              }
              // No fallback — clicking before pf-search is loaded does nothing
              // visible. After search-drawer milestone lands, this just works.
            } else if (action === 'cart-open') {
              if (window.pfCart && typeof window.pfCart.open === 'function') {
                e.preventDefault();
                window.pfCart.open();
              } else {
                // Fallback while the cart drawer isn't on the page yet.
                e.preventDefault();
                window.location.href = window.Shopify?.routes?.root
                  ? window.Shopify.routes.root + 'cart'
                  : '/cart';
              }
            }
          }, { signal });
        });
      }
    }

    customElements.define('pf-header', PfHeader);
  }

  // ---------------------------------------------------------------------------
  // <pf-collection-nav>
  // Horizontal pill / underline / plain row of collection links. Backed by
  // Swiper in free-mode so it's draggable on desktop (grab cursor + mouse
  // drag) and swipeable on touch. With watchOverflow: true, Swiper locks
  // itself when there's nothing to scroll, so short lists behave like
  // static rows.
  // ---------------------------------------------------------------------------
  if (!customElements.get('pf-collection-nav')) {
    class PfCollectionNav extends HTMLElement {
      connectedCallback() {
        this.swiperEl = this.querySelector('[data-pf-cn-swiper]');
        if (!this.swiperEl) return;

        const baseGapDesktop = parseInt(this.dataset.gapDesktop, 10) || 8;
        const baseGapMobile = parseInt(this.dataset.gapMobile, 10) || 6;

        // Underline+count mode puts the product count at `right: -10px` on
        // each slide as a small superscript — without extra space between
        // slides, the count visually claws into the next slide's text. Pad
        // the gap so the slides stay legibly separated.
        const isUnderline = this.classList.contains('pf-collection-nav--style-underline');
        const hasCount = this.classList.contains('pf-collection-nav--has-count');
        const bonus = isUnderline && hasCount ? 16 : 0;

        this.swiper = new window.Swiper(this.swiperEl, {
          slidesPerView: 'auto',
          spaceBetween: baseGapMobile + bonus,
          freeMode: true,
          grabCursor: true,
          mousewheel: { forceToAxis: true },
          watchOverflow: true,
          resizeObserver: true,
          breakpoints: {
            750: { spaceBetween: baseGapDesktop + bonus },
          },
        });
      }

      disconnectedCallback() {
        if (this.swiper) {
          try { this.swiper.destroy(true, true); } catch (e) { /* noop */ }
        }
      }
    }

    customElements.define('pf-collection-nav', PfCollectionNav);
  }

  // ---------------------------------------------------------------------------
  // <pf-collection-products-grid>
  // The collection page's main section: filter sidebar (or drawer) + product
  // grid + top bar (density picker, active chips, sort, filter button) +
  // pagination footer (pagination | load_more | infinite).
  //
  // Filter form submits via the Sections API so the products grid swaps in
  // place without a full page reload. The URL is kept in sync via
  // history.replaceState so back/forward and copy/paste both work.
  //
  // View density is per-user (localStorage), not per-merchant. The merchant
  // picks the default in the schema; the user can override live via the
  // density buttons. Persisted under a single key shared across collections.
  // ---------------------------------------------------------------------------
  if (!customElements.get('pf-collection-products-grid')) {
    const DENSITY_KEY = 'pf:collection-density';

    class PfCollectionProductsGrid extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.filterLayout = this.dataset.filterLayout;
        this.paginationMode = this.dataset.paginationMode;
        this.minCols = parseInt(this.dataset.minColumnsDesktop, 10) || 3;
        this.maxCols = parseInt(this.dataset.maxColumnsDesktop, 10) || 6;
        this.defaultCols = parseInt(this.dataset.defaultColumnsDesktop, 10) || 4;

        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.applyStoredDensity();
        this.bindDensity(signal);
        this.bindDrawer(signal);
        this.bindFilterForms(signal);
        this.bindSort(signal);
        this.bindPagination(signal);
        this.bindPriceRanges();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.infiniteObserver?.disconnect();
      }

      // ---- Density ----
      applyStoredDensity() {
        try {
          const stored = parseInt(localStorage.getItem(DENSITY_KEY), 10);
          if (stored && stored >= this.minCols && stored <= this.maxCols) {
            this.setDensity(stored, { persist: false });
          }
        } catch (e) { /* storage might be blocked */ }
      }

      bindDensity(signal) {
        const buttons = this.querySelectorAll('[data-pf-density-cols]');
        buttons.forEach((btn) => {
          btn.addEventListener('click', () => {
            const cols = parseInt(btn.dataset.pfDensityCols, 10);
            if (cols) this.setDensity(cols);
          }, { signal });
        });
      }

      setDensity(cols, { persist = true } = {}) {
        this.style.setProperty('--pf-cpg-cols-d', String(cols));
        this.querySelectorAll('[data-pf-density-cols]').forEach((btn) => {
          const matches = parseInt(btn.dataset.pfDensityCols, 10) === cols;
          btn.setAttribute('aria-pressed', matches ? 'true' : 'false');
        });
        const current = this.querySelector('[data-pf-density-current]');
        if (current) {
          // Localized format string lives in markup; we just swap the number.
          const tmpl = current.textContent.replace(/\d+/, '##');
          current.textContent = tmpl.replace('##', String(cols));
        }
        if (persist) {
          try { localStorage.setItem(DENSITY_KEY, String(cols)); } catch (e) { /* noop */ }
        }
      }

      // ---- Drawer ----
      bindDrawer(signal) {
        this.drawer = this.querySelector('[data-pf-drawer]');
        const openBtn = this.querySelector('[data-pf-filter-open]');
        const closers = this.querySelectorAll('[data-pf-drawer-close]');

        if (openBtn && this.drawer) {
          openBtn.addEventListener('click', () => this.openDrawer(), { signal });
        }
        closers.forEach((el) => {
          el.addEventListener('click', () => this.closeDrawer(), { signal });
        });
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.drawer && this.drawer.classList.contains('is-open')) {
            this.closeDrawer();
          }
        }, { signal });
      }

      openDrawer() {
        if (!this.drawer) return;
        this.drawer.hidden = false;
        // Force a reflow so the panel's translateX(-100%) starting position is
        // committed before we add .is-open which transitions it to 0.
        void this.drawer.offsetHeight;
        this.drawer.classList.add('is-open');
        this.drawer.setAttribute('aria-hidden', 'false');
        document.documentElement.style.overflow = 'hidden';
      }

      closeDrawer() {
        // Always release the document scroll lock. When a filter submit
        // swaps [data-pf-drawer] via AJAX, the new drawer node arrives in
        // its default-closed state — no `is-open` class — so the early
        // return below would otherwise skip the unlock, leaving the page
        // scrollbar hidden after a filter application.
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';

        if (!this.drawer) return;
        if (!this.drawer.classList.contains('is-open')) return;
        this.drawer.classList.remove('is-open');
        this.drawer.setAttribute('aria-hidden', 'true');

        const panel = this.drawer.querySelector('.pf-cpg__drawer-panel');
        const onEnd = () => {
          this.drawer.hidden = true;
          panel?.removeEventListener('transitionend', onEnd);
        };
        if (panel) {
          panel.addEventListener('transitionend', onEnd, { once: true, signal: this.abortController.signal });
          // Safety net — fall back to a fixed timeout if no transition fires.
          setTimeout(onEnd, 320);
        } else {
          this.drawer.hidden = true;
        }
      }

      // ---- Filter forms (sidebar + drawer + chip removals) ----
      bindFilterForms(signal) {
        // Auto-submit on change for sidebar form (live filtering).
        // Drawer form needs explicit Apply (avoid live AJAX while user is
        // ticking multiple boxes in the drawer).
        this.querySelectorAll('[data-pf-filter-form]').forEach((form) => {
          const isSidebar = form.classList.contains('pf-cf--sidebar');

          if (isSidebar) {
            form.addEventListener('change', (e) => {
              // Don't auto-submit on price-range slider live drags — wait for
              // commit (change event on number inputs / pointerup is fine).
              if (e.target.matches('[data-pf-price-thumb]') && e.type === 'input') return;
              this.submitFiltersFromForm(form);
            }, { signal });
            form.addEventListener('submit', (e) => {
              e.preventDefault();
              this.submitFiltersFromForm(form);
            }, { signal });
          } else {
            // Drawer: explicit submit + clear link.
            form.addEventListener('submit', (e) => {
              e.preventDefault();
              this.submitFiltersFromForm(form, { closeDrawerAfter: true });
            }, { signal });
          }
        });

        // Active filter chips — intercept clicks, fetch the remove URL.
        this.querySelectorAll('[data-pf-chip-remove], [data-pf-clear-all], [data-pf-filter-clear]').forEach((link) => {
          link.addEventListener('click', (e) => {
            const url = link.getAttribute('href');
            if (!url) return;
            e.preventDefault();
            this.fetchAndReplace(url, { closeDrawerAfter: true });
          }, { signal });
        });
      }

      submitFiltersFromForm(form, opts = {}) {
        const formData = new FormData(form);
        // Drop empty values to keep URLs clean.
        const params = new URLSearchParams();
        formData.forEach((value, key) => {
          if (value === '' || value == null) return;

          // Price filter contract: Shopify's URL params (filter.v.price.gte/
          // lte) accept the value in MAJOR currency units (e.g. 115 for
          // $115), not cents. Liquid's filter.min_value.value, on the other
          // hand, IS in cents — so the snippet divides by 100 for display.
          // Multiplying here would push 115 into the URL as 11500 and
          // Shopify would filter for $11,500. Pass the input value straight
          // through, validating it's a positive number.
          if (key === 'filter.v.price.gte' || key === 'filter.v.price.lte') {
            const num = parseFloat(value);
            if (Number.isFinite(num) && num > 0) {
              params.append(key, String(num));
            }
            return;
          }
          params.append(key, value);
        });
        const action = form.getAttribute('action') || window.location.pathname;
        const url = action + (params.toString() ? '?' + params.toString() : '');
        this.fetchAndReplace(url, opts);
      }

      // ---- Sort dropdown (top bar, sidebar layout only) ----
      bindSort(signal) {
        const sortSelect = this.querySelector('[data-pf-sort]');
        if (!sortSelect) return;
        sortSelect.addEventListener('change', () => {
          // Carry forward the current URL's filter params; just swap sort_by.
          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', sortSelect.value);
          url.searchParams.delete('page');
          this.fetchAndReplace(url.toString());
        }, { signal });
      }

      // ---- Pagination / load-more / infinite ----
      bindPagination(signal) {
        if (this.paginationMode === 'pagination') return;

        if (this.paginationMode === 'load_more') {
          this.bindLoadMore(signal);
        } else if (this.paginationMode === 'infinite') {
          this.bindInfinite();
        }
      }

      bindLoadMore(signal) {
        const btn = this.querySelector('[data-pf-load-more]');
        if (!btn) return;
        btn.addEventListener('click', () => this.appendNext(btn), { signal });
      }

      bindInfinite() {
        const sentinel = this.querySelector('[data-pf-infinite-sentinel]');
        if (!sentinel || !('IntersectionObserver' in window)) return;

        this.infiniteObserver?.disconnect();
        this.infiniteObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.appendNext(sentinel);
          });
        }, { rootMargin: '400px 0px' });
        this.infiniteObserver.observe(sentinel);
      }

      async appendNext(trigger) {
        const url = trigger.dataset.nextUrl;
        if (!url) return;
        if (trigger.dataset.loading === 'true') return;
        trigger.dataset.loading = 'true';

        // The infinite wrapper hosts the visible spinner. For load_more,
        // the button itself flips on its own `[data-loading="true"]` rule.
        const infiniteWrap = this.querySelector('[data-pf-infinite]');
        if (infiniteWrap && infiniteWrap.contains(trigger)) {
          infiniteWrap.setAttribute('data-loading', 'true');
        }

        try {
          const html = await this.fetchSectionHtml(url);
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newGrid = doc.querySelector('[data-pf-grid]');
          const newPaging = doc.querySelector('[data-pf-paging]');
          const currentGrid = this.querySelector('[data-pf-grid]');
          const currentPaging = this.querySelector('[data-pf-paging]');

          if (newGrid && currentGrid) {
            // Append the new cards (don't replace — accumulating list).
            Array.from(newGrid.children).forEach((node) => currentGrid.appendChild(node));
            this.rebindSwatches();
          }
          if (currentPaging && newPaging) {
            currentPaging.replaceWith(newPaging);
          } else if (currentPaging && !newPaging) {
            // No next page → strip the paging footer entirely.
            currentPaging.remove();
          }

          // Re-bind whichever mode now applies (load_more for the new button,
          // infinite for the new sentinel — same instance, fresh node).
          if (this.paginationMode === 'load_more') {
            this.bindLoadMore(this.abortController.signal);
          } else if (this.paginationMode === 'infinite') {
            this.bindInfinite();
          }
        } catch (err) {
          // Failure isn't user-actionable; log and let the trigger remain
          // clickable so they can retry.
          console.warn('[pf-cpg] appendNext failed', err);
        } finally {
          trigger.dataset.loading = 'false';
          if (infiniteWrap && infiniteWrap.isConnected) {
            infiniteWrap.removeAttribute('data-loading');
          }
        }
      }

      // ---- AJAX fetch + swap ----
      async fetchAndReplace(url, opts = {}) {
        this.setAttribute('data-loading', 'true');
        try {
          const html = await this.fetchSectionHtml(url);
          this.replaceFromHtml(html);
          // Update the address bar (without the &section_id suffix) so the
          // user can share / refresh the URL.
          const clean = url.replace(/[&?]section_id=[^&]+/, '').replace(/\?$/, '');
          window.history.replaceState({}, '', clean);
          if (opts.closeDrawerAfter) this.closeDrawer();
        } catch (err) {
          console.warn('[pf-cpg] fetchAndReplace failed', err);
          // Hard fallback — navigate normally so the user isn't stranded.
          window.location.href = url;
        } finally {
          this.removeAttribute('data-loading');
        }
      }

      async fetchSectionHtml(url) {
        const fetchUrl = url + (url.includes('?') ? '&' : '?') + 'section_id=' + encodeURIComponent(this.sectionId);
        const res = await fetch(fetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!res.ok) throw new Error('Section fetch failed: ' + res.status);
        return await res.text();
      }

      replaceFromHtml(html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        // When using ?section_id, Shopify returns the section's rendered
        // HTML. Find the custom element by tag name (id varies per render).
        const newSection = doc.querySelector('pf-collection-products-grid');
        if (!newSection) {
          console.warn('[pf-cpg] response had no pf-collection-products-grid element');
          return;
        }

        // Capture each filter accordion's current open/closed state, keyed
        // by data-pf-filter-key. We restore these after the swap so the
        // user's manually-toggled accordions don't snap back to whatever
        // open-by-default state Liquid renders (which is open-when-active
        // — fine for filters they just changed, jarring for filters they
        // were exploring).
        const accordionState = {};
        this.querySelectorAll('[data-pf-filter-key]').forEach((el) => {
          accordionState[el.dataset.pfFilterKey] = el.open;
        });

        // Swap entire panels in place — one node per panel, no double-writes.
        // The grid + paging + empty-state all live inside [data-pf-main],
        // so swapping that single node covers all three. The sidebar form
        // is swapped too so its disabled-value markers / counts reflect
        // the new filtered state.
        const panels = [
          '[data-pf-topbar]',
          '[data-pf-sidebar]',
          '[data-pf-main]',
          '[data-pf-drawer]',
        ];
        panels.forEach((sel) => {
          const incoming = newSection.querySelector(sel);
          const current = this.querySelector(sel);
          if (incoming && current) {
            current.replaceWith(incoming);
          }
        });

        // Restore each accordion's open state. Only override when we have
        // a recorded value — new filters added to Search & Discovery between
        // page load and submit fall through to the server's default.
        this.querySelectorAll('[data-pf-filter-key]').forEach((el) => {
          const key = el.dataset.pfFilterKey;
          if (Object.prototype.hasOwnProperty.call(accordionState, key)) {
            el.open = accordionState[key];
          }
        });

        // Re-bind everything dynamic on the freshly-injected nodes.
        this.rebindAll();
      }

      rebindAll() {
        // Listeners attached via { signal } from the controller still work
        // for the host element-level listeners we set on `this`. But chips,
        // drawer-close buttons inside the swapped drawer, etc. lost their
        // listeners with the node swap. The simplest robust fix is to abort
        // and rebuild the controller for the dynamic listener set.
        this.abortController.abort();
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        // Re-bind everything dynamic on the freshly-injected nodes.
        this.bindDrawer(signal);
        this.bindFilterForms(signal);
        this.bindSort(signal);
        this.bindPagination(signal);
        this.bindDensity(signal);
        this.bindPriceRanges();
        this.rebindSwatches();
      }

      // Product card swatches — same image swap as <pf-collection-products>.
      rebindSwatches() {
        const cards = this.querySelectorAll('[data-pf-grid] .pf-product-card');
        cards.forEach((card) => {
          if (card.dataset.pfSwatchesBound === 'true') return;
          const swatches = card.querySelectorAll('.pf-product-card__swatch');
          if (swatches.length === 0) return;
          const primary = card.querySelector('.pf-product-card__image--primary');
          if (!primary) return;
          card.dataset.pfSwatchesBound = 'true';

          const originalSrc = primary.getAttribute('src');
          const originalSrcset = primary.getAttribute('srcset');

          swatches.forEach((swatch) => {
            swatch.addEventListener('click', (event) => {
              event.preventDefault();
              event.stopPropagation();
              const newSrc = swatch.dataset.imageSrc;
              const newSrcset = swatch.dataset.imageSrcset;
              if (!newSrc) return;
              if (newSrcset) primary.setAttribute('srcset', newSrcset);
              primary.setAttribute('src', newSrc);
              swatches.forEach((s) => s.removeAttribute('aria-pressed'));
              swatch.setAttribute('aria-pressed', 'true');
            });
          });

          card.addEventListener('mouseleave', () => {
            const active = card.querySelector('.pf-product-card__swatch[aria-pressed="true"]');
            if (!active) {
              primary.setAttribute('src', originalSrc);
              if (originalSrcset) primary.setAttribute('srcset', originalSrcset);
            }
          });
        });
      }

      // ---- Price ranges (dual thumb + mirrored number inputs) ----
      bindPriceRanges() {
        this.querySelectorAll('[data-pf-price]').forEach((wrap) => this.initPriceRange(wrap));
      }

      initPriceRange(wrap) {
        const minThumb = wrap.querySelector('[data-pf-price-thumb="min"]');
        const maxThumb = wrap.querySelector('[data-pf-price-thumb="max"]');
        const minInput = wrap.querySelector('[data-pf-price-input="min"]');
        const maxInput = wrap.querySelector('[data-pf-price-input="max"]');
        const fill = wrap.querySelector('[data-pf-price-fill]');
        if (!minThumb || !maxThumb || !minInput || !maxInput) return;

        // Everything below operates in DISPLAY units (major currency, e.g.
        // 500 for €500). The Liquid snippet has already converted from
        // Shopify's cents to display units for the slider/input attrs.
        // Form submission converts back to cents.
        const max = parseFloat(wrap.dataset.max) || 0;
        const min = parseFloat(wrap.dataset.min) || 0;

        // Force the slider thumb values from the wrapper's data-cur-* attrs.
        // The HTML `value` attribute on a freshly-injected <input type="range">
        // node sometimes doesn't apply (the property defaults to mid-range
        // when min/max haven't been parsed yet, depending on the node-swap
        // path). Setting the `.value` property explicitly bypasses that.
        const curMin = parseFloat(wrap.dataset.curMin);
        const curMax = parseFloat(wrap.dataset.curMax);
        if (!Number.isNaN(curMin)) minThumb.value = curMin;
        if (!Number.isNaN(curMax)) maxThumb.value = curMax;

        const pct = (val) => {
          if (max === min) return 0;
          return ((val - min) / (max - min)) * 100;
        };

        const updateFill = () => {
          if (!fill || max === min) return;
          const lo = parseFloat(minThumb.value);
          const hi = parseFloat(maxThumb.value);
          const loPct = pct(lo);
          const hiPct = pct(hi);
          // Use left + width (clearer than left + right). Clamp width to 0
          // when min > max so the bar collapses rather than going negative.
          fill.style.left = loPct + '%';
          fill.style.width = Math.max(0, hiPct - loPct) + '%';
          fill.style.right = 'auto';
        };

        const writeInputs = (lo, hi) => {
          // Blank when at the range edges so we don't push unnecessary
          // ?filter.v.price.gte=0 params into the URL.
          minInput.value = lo <= min ? '' : String(lo);
          maxInput.value = hi >= max ? '' : String(hi);
        };

        const syncFromThumbs = () => {
          let lo = parseFloat(minThumb.value);
          let hi = parseFloat(maxThumb.value);
          if (lo > hi) {
            if (this._lastTouched === 'max') {
              minThumb.value = hi;
              lo = hi;
            } else {
              maxThumb.value = lo;
              hi = lo;
            }
          }
          writeInputs(lo, hi);
          updateFill();
        };

        const syncFromInputs = () => {
          let lo = parseFloat(minInput.value);
          let hi = parseFloat(maxInput.value);
          if (Number.isNaN(lo)) lo = min;
          if (Number.isNaN(hi)) hi = max;
          lo = Math.max(min, Math.min(lo, max));
          hi = Math.max(min, Math.min(hi, max));
          if (lo > hi) {
            if (this._lastTouched === 'max') lo = hi;
            else hi = lo;
          }
          minThumb.value = lo;
          maxThumb.value = hi;
          writeInputs(lo, hi);
          updateFill();
        };

        minThumb.addEventListener('input', () => { this._lastTouched = 'min'; syncFromThumbs(); });
        maxThumb.addEventListener('input', () => { this._lastTouched = 'max'; syncFromThumbs(); });
        minInput.addEventListener('change', () => { this._lastTouched = 'min'; syncFromInputs(); });
        maxInput.addEventListener('change', () => { this._lastTouched = 'max'; syncFromInputs(); });
        updateFill();
      }
    }

    customElements.define('pf-collection-products-grid', PfCollectionProductsGrid);
  }

  // ---------------------------------------------------------------------------
  // <pf-product-details>
  // The orchestrator for the Product Details page section. Coordinates:
  //   - Variant changes (radio click → resolve combo → fetch via Sections API
  //     using section.id → swap section markup → preserve scroll position)
  //   - Add-to-cart form submission (AJAX → window.pfCart.addToCart → auto-open
  //     cart drawer based on `data-auto-open-drawer`)
  //   - Tippy.js wiring for [data-pf-tooltip] descendants
  // ---------------------------------------------------------------------------
  if (!customElements.get('pf-product-details')) {
    class PfProductDetails extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productHandle = this.dataset.productHandle;
        this.productUrl = this.dataset.productUrl;
        this.autoOpenDrawer = this.dataset.autoOpenDrawer === 'true';
        this.filterMediaByVariant = this.dataset.filterMediaByVariant === 'true';
        try {
          this.variants = JSON.parse(this.dataset.variants || '[]');
        } catch (e) {
          this.variants = [];
        }
        this.currentVariantId = parseInt(this.dataset.currentVariantId, 10);

        this.abortController = new AbortController();
        this.fetchController = null;

        this.bindVariantChange();
        this.bindAtcSubmit();
        this.bindQtyButtons();
        this.initTooltips();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.fetchController?.abort();
        // Destroy any tippy instances we created on descendants.
        this.querySelectorAll('[data-pf-tooltip]').forEach((el) => {
          if (el._tippy) el._tippy.destroy();
        });
      }

      bindVariantChange() {
        this.addEventListener(
          'pf:variant-change',
          (e) => {
            const newVariantId = e.detail?.variantId;
            if (!newVariantId || newVariantId === this.currentVariantId) return;
            this.fetchAndSwap(newVariantId);
          },
          { signal: this.abortController.signal }
        );
      }

      bindAtcSubmit() {
        this.addEventListener(
          'submit',
          (e) => {
            const form = e.target.closest('[data-pf-pdp-atc-form]');
            if (!form) return;
            e.preventDefault();
            this.handleAtcSubmit(form);
          },
          { signal: this.abortController.signal }
        );
      }

      bindQtyButtons() {
        this.addEventListener(
          'click',
          (e) => {
            const minus = e.target.closest('[data-pf-pdp-qty-minus]');
            const plus = e.target.closest('[data-pf-pdp-qty-plus]');
            if (!minus && !plus) return;

            const wrap = (minus || plus).closest('[data-pf-pdp-qty]');
            const input = wrap?.querySelector('[data-pf-pdp-qty-input]');
            if (!input) return;

            const current = parseInt(input.value, 10) || 1;
            const next = plus ? current + 1 : current - 1;
            const min = parseInt(input.getAttribute('min'), 10) || 1;
            input.value = String(Math.max(min, next));
            input.dispatchEvent(new Event('change', { bubbles: true }));
          },
          { signal: this.abortController.signal }
        );
      }

      async handleAtcSubmit(form) {
        const btn = form.querySelector('[data-pf-pdp-atc-btn]');
        const variantIdInput = form.querySelector('[data-pf-pdp-atc-variant-id]');
        const qtyInput = form.querySelector('[data-pf-pdp-qty-input]');
        const variantId = variantIdInput ? parseInt(variantIdInput.value, 10) : this.currentVariantId;
        const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

        if (!variantId) return;
        if (btn) btn.setAttribute('data-loading', 'true');

        try {
          if (window.pfCart && typeof window.pfCart.addToCart === 'function') {
            await window.pfCart.addToCart(variantId, qty);
            if (this.autoOpenDrawer) {
              window.pfCart.open();
            }
          } else {
            // Fallback: classic Shopify form submit
            form.submit();
          }
        } catch (err) {
          console.warn('[pf-product-details] ATC failed', err);
        } finally {
          if (btn) btn.removeAttribute('data-loading');
        }
      }

      async fetchAndSwap(variantId) {
        this.currentVariantId = variantId;

        // Optimistically update any visible variant-id inputs so the user
        // can't trigger ATC with a stale id between this point and AJAX.
        this.querySelectorAll('[data-pf-pdp-atc-variant-id]').forEach((input) => {
          input.value = variantId;
        });

        // Update the browser URL canonically — preserves bookmarks/shares.
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', String(variantId));
          window.history.replaceState({}, '', url.toString());
        } catch (e) {
          // ignore; URL update is a nice-to-have
        }

        if (this.fetchController) this.fetchController.abort();
        this.fetchController = new AbortController();

        const url = `${this.productUrl}?variant=${variantId}&section_id=${encodeURIComponent(this.sectionId)}`;

        try {
          const res = await fetch(url, { signal: this.fetchController.signal });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const html = await res.text();
          this.swapFromHtml(html);
        } catch (err) {
          if (err.name !== 'AbortError') {
            console.warn('[pf-product-details] section fetch failed', err);
          }
        }
      }

      swapFromHtml(html) {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const incoming = tmp.querySelector(`pf-product-details[data-section-id="${this.sectionId}"]`);
        if (!incoming) {
          // Sometimes Shopify wraps the section response in a section wrapper;
          // try a more permissive selector.
          const fallback = tmp.querySelector('pf-product-details');
          if (!fallback) return;
          this.replaceWith(fallback);
          return;
        }
        this.replaceWith(incoming);
      }

      initTooltips() {
        if (typeof window.tippy !== 'function') return;
        this.querySelectorAll('[data-pf-tooltip]').forEach((el) => {
          const content = el.getAttribute('data-pf-tooltip');
          if (!content || el._tippy) return;
          window.tippy(el, {
            content,
            allowHTML: false,
            theme: 'pf-pdp',
            placement: 'top',
            arrow: true,
            maxWidth: 280,
            duration: [120, 80],
            interactive: false,
          });
        });
      }
    }
    customElements.define('pf-product-details', PfProductDetails);
  }

  // ---------------------------------------------------------------------------
  // <pf-pdp-media-gallery>
  // Owns the product gallery's Swiper instances (main + optional thumbs),
  // fslightbox refresh, and variant-filtering. Re-runs on every section
  // re-render because the parent <pf-product-details> rebuilds this node.
  // ---------------------------------------------------------------------------
  if (!customElements.get('pf-pdp-media-gallery')) {
    class PfPdpMediaGallery extends HTMLElement {
      connectedCallback() {
        this.layout = this.dataset.layout;
        this.thumbs = this.dataset.thumbs;
        this.enableZoom = this.dataset.enableZoom === 'true';
        this.enableLightbox = this.dataset.enableLightbox === 'true';
        this.currentVariantId = parseInt(this.dataset.currentVariantId, 10);

        // Skip Swiper init only when layout=grid AND viewport is desktop —
        // grid mode on mobile falls back to slider behavior.
        this.desktopMql = window.matchMedia('(min-width: 990px)');
        this.applyLayoutForViewport();

        // Re-init / destroy when crossing the desktop breakpoint so that
        // grid mode on desktop and slider mode on mobile both behave
        // correctly without a page refresh.
        this._mqlListener = () => this.applyLayoutForViewport();
        if (typeof this.desktopMql.addEventListener === 'function') {
          this.desktopMql.addEventListener('change', this._mqlListener);
        } else if (typeof this.desktopMql.addListener === 'function') {
          // Safari < 14 fallback
          this.desktopMql.addListener(this._mqlListener);
        }

        if (this.enableLightbox && typeof window.refreshFsLightbox === 'function') {
          // Defer one tick so any newly-added <a data-fslightbox> nodes
          // are in the DOM when fslightbox scans the document.
          requestAnimationFrame(() => window.refreshFsLightbox());
        }
      }

      disconnectedCallback() {
        if (this._mqlListener && this.desktopMql) {
          if (typeof this.desktopMql.removeEventListener === 'function') {
            this.desktopMql.removeEventListener('change', this._mqlListener);
          } else if (typeof this.desktopMql.removeListener === 'function') {
            this.desktopMql.removeListener(this._mqlListener);
          }
        }
        this.destroySwipers();
      }

      destroySwipers() {
        if (this.mainSwiper) {
          this.mainSwiper.destroy(true, true);
          this.mainSwiper = null;
        }
        if (this.thumbsSwiper) {
          this.thumbsSwiper.destroy(true, true);
          this.thumbsSwiper = null;
        }
      }

      applyLayoutForViewport() {
        const isDesktop = this.desktopMql.matches;
        const isGridDesktop = this.layout === 'grid' && isDesktop;

        if (isGridDesktop) {
          // Grid view on desktop — no Swiper needed; destroy any existing one.
          this.destroySwipers();
          return;
        }

        // Slider view (always on mobile; on desktop when layout=slider).
        if (!this.mainSwiper) {
          this.initSlider();
        }
      }

      initSlider() {
        if (typeof window.Swiper !== 'function') return;

        const mainEl = this.querySelector('[data-pf-pdp-media-main]');
        const thumbsEl = this.querySelector('[data-pf-pdp-media-thumbs]');
        const prevBtn = this.querySelector('[data-pf-pdp-media-prev]');
        const nextBtn = this.querySelector('[data-pf-pdp-media-next]');
        const paginationEl = this.querySelector('[data-pf-pdp-media-pagination]');

        if (!mainEl) return;

        const mainOpts = {
          slidesPerView: 1,
          spaceBetween: 0,
          watchOverflow: true,
          loop: true,
          zoom: this.enableZoom ? { maxRatio: 3, toggle: true } : false,
          navigation: {
            prevEl: prevBtn || null,
            nextEl: nextBtn || null,
          },
        };

        if (paginationEl) {
          mainOpts.pagination = {
            el: paginationEl,
            clickable: true,
          };
        }

        if (thumbsEl) {
          const thumbsConfig = {
            slidesPerView: 'auto',
            spaceBetween: 8,
            watchSlidesProgress: true,
            freeMode: this.thumbs === 'below',
          };
          if (this.thumbs === 'side') {
            thumbsConfig.direction = window.matchMedia('(max-width: 989px)').matches ? 'horizontal' : 'vertical';
          }
          this.thumbsSwiper = new window.Swiper(thumbsEl, thumbsConfig);
          mainOpts.thumbs = { swiper: this.thumbsSwiper };
        }

        this.mainSwiper = new window.Swiper(mainEl, mainOpts);
      }
    }
    customElements.define('pf-pdp-media-gallery', PfPdpMediaGallery);
  }

  // ---------------------------------------------------------------------------
  // <pf-pdp-variants>
  // Handles option-radio changes — resolves the new variant id from the
  // parent's variants array and dispatches `pf:variant-change` upward. Also
  // owns the size-chart <dialog>.
  // ---------------------------------------------------------------------------
  if (!customElements.get('pf-pdp-variants')) {
    class PfPdpVariants extends HTMLElement {
      connectedCallback() {
        this.host = this.closest('pf-product-details');
        if (!this.host) return;

        this.abortController = new AbortController();

        this.bindOptionInputs();
        this.bindSizeChartDialog();
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      bindOptionInputs() {
        const { signal } = this.abortController;
        this.addEventListener(
          'change',
          (e) => {
            const input = e.target.closest('[data-pf-pdp-option-input]');
            if (!input) return;
            this.handleOptionChange();
          },
          { signal }
        );
      }

      handleOptionChange() {
        const selectedValues = [];
        const groupedRadios = this.querySelectorAll('[data-pf-pdp-option-input]:checked');
        groupedRadios.forEach((radio) => {
          const idx = parseInt(radio.dataset.optionIndex, 10);
          selectedValues[idx] = radio.value;
        });

        // Resolve to a concrete variant using the parent's variants array.
        const variants = this.host.variants || [];
        const matched = variants.find((v) => {
          const opts = [v.option1, v.option2, v.option3];
          for (let i = 0; i < selectedValues.length; i += 1) {
            if (selectedValues[i] != null && opts[i] !== selectedValues[i]) {
              return false;
            }
          }
          return true;
        });
        if (!matched) return;

        this.dispatchEvent(
          new CustomEvent('pf:variant-change', {
            bubbles: true,
            detail: { variantId: matched.id },
          })
        );
      }

      bindSizeChartDialog() {
        const dialog = this.querySelector('[data-pf-pdp-size-chart-dialog]');
        if (!dialog) return;

        const openBtns = this.querySelectorAll('[data-pf-pdp-size-chart-open]');
        const closeBtn = this.querySelector('[data-pf-pdp-size-chart-close]');
        const { signal } = this.abortController;

        openBtns.forEach((btn) =>
          btn.addEventListener(
            'click',
            () => {
              if (typeof dialog.showModal === 'function') dialog.showModal();
              else dialog.setAttribute('open', '');
            },
            { signal }
          )
        );

        closeBtn?.addEventListener('click', () => dialog.close(), { signal });

        // Backdrop click closes
        dialog.addEventListener(
          'click',
          (e) => {
            if (e.target === dialog) dialog.close();
          },
          { signal }
        );
      }
    }
    customElements.define('pf-pdp-variants', PfPdpVariants);
  }

  // ---------------------------------------------------------------------------
  // <pf-pdp-accordion>
  // Smooth grid-rows 0fr/1fr accordion toggle.
  // ---------------------------------------------------------------------------
  if (!customElements.get('pf-pdp-accordion')) {
    class PfPdpAccordion extends HTMLElement {
      connectedCallback() {
        this.trigger = this.querySelector('[data-pf-pdp-accordion-trigger]');
        this.panel = this.querySelector('[data-pf-pdp-accordion-panel]');
        if (!this.trigger || !this.panel) return;

        // Initialize panel state to match aria-expanded
        const expanded = this.trigger.getAttribute('aria-expanded') === 'true';
        this.setPanelState(expanded);

        this.abortController = new AbortController();
        this.trigger.addEventListener(
          'click',
          () => {
            const isOpen = this.trigger.getAttribute('aria-expanded') === 'true';
            this.trigger.setAttribute('aria-expanded', String(!isOpen));
            this.setPanelState(!isOpen);
          },
          { signal: this.abortController.signal }
        );
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      setPanelState(open) {
        if (open) {
          this.panel.setAttribute('data-open', 'true');
          this.panel.removeAttribute('aria-hidden');
        } else {
          this.panel.removeAttribute('data-open');
          this.panel.setAttribute('aria-hidden', 'true');
        }
      }
    }
    customElements.define('pf-pdp-accordion', PfPdpAccordion);
  }

  // ---------------------------------------------------------------------------
  // <pf-pdp-ctl-card>
  // Per-card variant resolution + ADD button for the Complete the Look block.
  // ---------------------------------------------------------------------------
  if (!customElements.get('pf-pdp-ctl-card')) {
    class PfPdpCtlCard extends HTMLElement {
      connectedCallback() {
        try {
          this.variants = JSON.parse(this.dataset.variants || '[]');
        } catch (e) {
          this.variants = [];
        }
        this.currentVariantId = parseInt(this.dataset.currentVariantId, 10);
        this.currentVariant = this.variants.find((v) => v.id === this.currentVariantId) || this.variants[0] || null;

        this.refs = {
          price: this.querySelector('[data-pf-pdp-ctl-price]'),
          compare: this.querySelector('[data-pf-pdp-ctl-compare]'),
          save: this.querySelector('[data-pf-pdp-ctl-save]'),
          color: this.querySelector('[data-pf-pdp-ctl-color]'),
          add: this.querySelector('[data-pf-pdp-ctl-add]'),
          media: this.querySelector('[data-pf-pdp-ctl-card-media] img'),
        };

        this.abortController = new AbortController();
        this.bindOptionButtons();
        this.bindAddButton();
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      bindOptionButtons() {
        const { signal } = this.abortController;
        this.addEventListener(
          'click',
          (e) => {
            const btn = e.target.closest('[data-pf-pdp-ctl-option-btn]');
            if (!btn || btn.classList.contains('pf-pdp-ctl-card__option-btn--unavailable')) return;
            this.handleOptionClick(btn);
          },
          { signal }
        );
      }

      bindAddButton() {
        const { signal } = this.abortController;
        this.refs.add?.addEventListener(
          'click',
          async () => {
            if (!this.currentVariant || !this.currentVariant.available) return;
            try {
              this.refs.add.disabled = true;
              if (window.pfCart && typeof window.pfCart.addToCart === 'function') {
                await window.pfCart.addToCart(this.currentVariant.id, 1);
                window.pfCart.open();
              }
            } catch (err) {
              console.warn('[pf-pdp-ctl-card] add failed', err);
            } finally {
              this.refs.add.disabled = !this.currentVariant?.available;
            }
          },
          { signal }
        );
      }

      handleOptionClick(btn) {
        const optionIndex = parseInt(btn.dataset.optionIndex, 10);
        const optionValue = btn.dataset.optionValue;
        if (!Number.isFinite(optionIndex) || optionValue == null || !this.currentVariant) return;

        // Substitute this option value into the current variant's option vector.
        const newOpts = [this.currentVariant.option1, this.currentVariant.option2, this.currentVariant.option3];
        newOpts[optionIndex] = optionValue;

        const next = this.variants.find((v) =>
          (v.option1 == null || v.option1 === newOpts[0]) &&
          (v.option2 == null || v.option2 === newOpts[1]) &&
          (v.option3 == null || v.option3 === newOpts[2])
        );
        if (!next) return;

        this.currentVariant = next;
        this.currentVariantId = next.id;
        this.updateUi();
      }

      updateUi() {
        const v = this.currentVariant;
        if (!v) return;

        // Selected state on buttons within the same option row
        const optionGroups = this.querySelectorAll('[data-option-index]');
        optionGroups.forEach((row) => {
          const idx = parseInt(row.dataset.optionIndex, 10);
          const targetValue = [v.option1, v.option2, v.option3][idx];
          row.querySelectorAll('[data-pf-pdp-ctl-option-btn]').forEach((b) => {
            const isSel = b.dataset.optionValue === targetValue;
            b.classList.toggle('pf-pdp-ctl-card__option-btn--selected', isSel);
          });
        });

        if (this.refs.price) this.refs.price.textContent = formatMoney(v.price);
        if (this.refs.compare) {
          if (v.compare_at_price && v.compare_at_price > v.price) {
            this.refs.compare.textContent = formatMoney(v.compare_at_price);
            this.refs.compare.style.display = '';
          } else {
            this.refs.compare.style.display = 'none';
          }
        }
        if (this.refs.save) {
          if (v.compare_at_price && v.compare_at_price > v.price) {
            const savings = v.compare_at_price - v.price;
            this.refs.save.style.display = '';
            // Preserve "SAVE" prefix label structure if it was server-rendered.
            this.refs.save.textContent = `SAVE ${formatMoney(savings)}`;
          } else {
            this.refs.save.style.display = 'none';
          }
        }
        if (this.refs.media && v.featured_image && v.featured_image.src) {
          this.refs.media.src = v.featured_image.src;
        }
        if (this.refs.add) this.refs.add.disabled = !v.available;
      }
    }
    customElements.define('pf-pdp-ctl-card', PfPdpCtlCard);
  }

  // ---------------------------------------------------------------------------
  // <pf-pdp-ctl-slider>
  // Swiper for the "Complete the Look" slider layout. slidesPerView: 'auto'
  // for variable-width cards.
  // ---------------------------------------------------------------------------
  if (!customElements.get('pf-pdp-ctl-slider')) {
    class PfPdpCtlSlider extends HTMLElement {
      connectedCallback() {
        if (typeof window.Swiper !== 'function') return;
        this.swiper = new window.Swiper(this, {
          slidesPerView: 'auto',
          spaceBetween: 12,
          freeMode: true,
          grabCursor: true,
          watchOverflow: true,
          mousewheel: { forceToAxis: true },
        });
      }
      disconnectedCallback() {
        this.swiper?.destroy(true, true);
      }
    }
    customElements.define('pf-pdp-ctl-slider', PfPdpCtlSlider);
  }

  // ---------------------------------------------------------------------------
  // <pf-search>
  // The search results page section. State-driven (idle / empty / results) —
  // the section file decides which markup to render based on `search.performed`
  // / `search.results_count`. This element only attaches interaction behavior
  // for the 'results' state: filter form, sort dropdown, chip removals,
  // drawer open/close, pagination modes (load_more / infinite). The 'idle'
  // and 'empty' states are pure forms — no JS needed; the browser handles
  // submit and navigates to /search?q=...
  //
  // Filter / sort / chip submits go through the Sections API so the results
  // region swaps in place without a full reload. `q` and `type` URL params
  // are preserved across mutations (added to the filter form's hidden
  // inputs by the snippet; preserved by the sort handler via URL parsing).
  //
  // Multi-bucket pagination (load_more / infinite): when articles and/or
  // pages buckets are present alongside products, appendNext fetches the
  // next page and appends each incoming bucket's children to the matching
  // current bucket — keyed by [data-pf-bucket].
  // ---------------------------------------------------------------------------
  if (!customElements.get('pf-search')) {
    class PfSearch extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.state = this.dataset.state;
        this.paginationMode = this.dataset.paginationMode;

        if (this.state !== 'results') return;

        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.bindDrawer(signal);
        this.bindFilterForms(signal);
        this.bindSort(signal);
        this.bindPagination(signal);
        this.bindPriceRanges();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.infiniteObserver?.disconnect();
      }

      // ---- Drawer (mobile filter) ----
      bindDrawer(signal) {
        this.drawer = this.querySelector('[data-pf-drawer]');
        const openBtn = this.querySelector('[data-pf-filter-open]');
        const closers = this.querySelectorAll('[data-pf-drawer-close]');

        if (openBtn && this.drawer) {
          openBtn.addEventListener('click', () => this.openDrawer(), { signal });
        }
        closers.forEach((el) => {
          el.addEventListener('click', () => this.closeDrawer(), { signal });
        });
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.drawer && this.drawer.classList.contains('is-open')) {
            this.closeDrawer();
          }
        }, { signal });
      }

      openDrawer() {
        if (!this.drawer) return;
        this.drawer.hidden = false;
        void this.drawer.offsetHeight;
        this.drawer.classList.add('is-open');
        this.drawer.setAttribute('aria-hidden', 'false');
        document.documentElement.style.overflow = 'hidden';
      }

      closeDrawer() {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';

        if (!this.drawer) return;
        if (!this.drawer.classList.contains('is-open')) return;
        this.drawer.classList.remove('is-open');
        this.drawer.setAttribute('aria-hidden', 'true');

        const panel = this.drawer.querySelector('.pf-search__drawer-panel');
        const onEnd = () => {
          this.drawer.hidden = true;
          panel?.removeEventListener('transitionend', onEnd);
        };
        if (panel) {
          panel.addEventListener('transitionend', onEnd, { once: true, signal: this.abortController.signal });
          setTimeout(onEnd, 320);
        } else {
          this.drawer.hidden = true;
        }
      }

      // ---- Filter forms ----
      bindFilterForms(signal) {
        this.querySelectorAll('[data-pf-filter-form]').forEach((form) => {
          const isSidebar = form.classList.contains('pf-cf--sidebar');

          if (isSidebar) {
            form.addEventListener('change', (e) => {
              if (e.target.matches('[data-pf-price-thumb]') && e.type === 'input') return;
              this.submitFiltersFromForm(form);
            }, { signal });
            form.addEventListener('submit', (e) => {
              e.preventDefault();
              this.submitFiltersFromForm(form);
            }, { signal });
          } else {
            form.addEventListener('submit', (e) => {
              e.preventDefault();
              this.submitFiltersFromForm(form, { closeDrawerAfter: true });
            }, { signal });
          }
        });

        this.querySelectorAll('[data-pf-chip-remove], [data-pf-clear-all], [data-pf-filter-clear]').forEach((link) => {
          link.addEventListener('click', (e) => {
            const url = link.getAttribute('href');
            if (!url) return;
            e.preventDefault();
            this.fetchAndReplace(url, { closeDrawerAfter: true });
          }, { signal });
        });
      }

      submitFiltersFromForm(form, opts = {}) {
        const formData = new FormData(form);
        const params = new URLSearchParams();
        formData.forEach((value, key) => {
          if (value === '' || value == null) return;
          if (key === 'filter.v.price.gte' || key === 'filter.v.price.lte') {
            const num = parseFloat(value);
            if (Number.isFinite(num) && num > 0) {
              params.append(key, String(num));
            }
            return;
          }
          params.append(key, value);
        });
        const action = form.getAttribute('action') || window.location.pathname;
        const url = action + (params.toString() ? '?' + params.toString() : '');
        this.fetchAndReplace(url, opts);
      }

      // ---- Sort dropdown ----
      bindSort(signal) {
        const sortSelect = this.querySelector('[data-pf-sort]');
        if (!sortSelect) return;
        sortSelect.addEventListener('change', () => {
          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', sortSelect.value);
          url.searchParams.delete('page');
          this.fetchAndReplace(url.toString());
        }, { signal });
      }

      // ---- Pagination ----
      bindPagination(signal) {
        if (this.paginationMode === 'pagination') return;
        if (this.paginationMode === 'load_more') {
          this.bindLoadMore(signal);
        } else if (this.paginationMode === 'infinite') {
          this.bindInfinite();
        }
      }

      bindLoadMore(signal) {
        const btn = this.querySelector('[data-pf-load-more]');
        if (!btn) return;
        btn.addEventListener('click', () => this.appendNext(btn), { signal });
      }

      bindInfinite() {
        const sentinel = this.querySelector('[data-pf-infinite-sentinel]');
        if (!sentinel || !('IntersectionObserver' in window)) return;

        this.infiniteObserver?.disconnect();
        this.infiniteObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.appendNext(sentinel);
          });
        }, { rootMargin: '400px 0px' });
        this.infiniteObserver.observe(sentinel);
      }

      async appendNext(trigger) {
        const url = trigger.dataset.nextUrl;
        if (!url) return;
        if (trigger.dataset.loading === 'true') return;
        trigger.dataset.loading = 'true';

        const infiniteWrap = this.querySelector('[data-pf-infinite]');
        if (infiniteWrap && infiniteWrap.contains(trigger)) {
          infiniteWrap.setAttribute('data-loading', 'true');
        }

        try {
          const html = await this.fetchSectionHtml(url);
          const doc = new DOMParser().parseFromString(html, 'text/html');

          // Append per-bucket — each [data-pf-bucket] in the response
          // matches the same-keyed bucket already on the page. Buckets
          // not yet on the page (e.g. articles only appear on later
          // pages) get inserted in their canonical order.
          const incomingBuckets = doc.querySelectorAll('[data-pf-bucket]');
          incomingBuckets.forEach((incoming) => {
            const key = incoming.getAttribute('data-pf-bucket');
            const currentBucket = this.querySelector(`[data-pf-bucket="${key}"]`);
            const incomingGrid = incoming.querySelector('[data-pf-grid]');
            if (!incomingGrid) return;

            if (currentBucket) {
              const currentGrid = currentBucket.querySelector('[data-pf-grid]');
              if (currentGrid) {
                Array.from(incomingGrid.children).forEach((node) => currentGrid.appendChild(node));
              }
            } else {
              // New bucket — append to main (it'll sit after the existing buckets).
              const main = this.querySelector('[data-pf-main]');
              if (main) main.appendChild(incoming);
            }
          });

          this.rebindSwatches();

          const newPaging = doc.querySelector('[data-pf-paging]');
          const currentPaging = this.querySelector('[data-pf-paging]');
          if (currentPaging && newPaging) {
            currentPaging.replaceWith(newPaging);
          } else if (currentPaging && !newPaging) {
            currentPaging.remove();
          }

          if (this.paginationMode === 'load_more') {
            this.bindLoadMore(this.abortController.signal);
          } else if (this.paginationMode === 'infinite') {
            this.bindInfinite();
          }
        } catch (err) {
          console.warn('[pf-search] appendNext failed', err);
        } finally {
          trigger.dataset.loading = 'false';
          if (infiniteWrap && infiniteWrap.isConnected) {
            infiniteWrap.removeAttribute('data-loading');
          }
        }
      }

      // ---- AJAX fetch + swap ----
      async fetchAndReplace(url, opts = {}) {
        this.setAttribute('data-loading', 'true');
        try {
          const html = await this.fetchSectionHtml(url);
          this.replaceFromHtml(html);
          const clean = url.replace(/[&?]section_id=[^&]+/, '').replace(/\?$/, '');
          window.history.replaceState({}, '', clean);
          if (opts.closeDrawerAfter) this.closeDrawer();
        } catch (err) {
          console.warn('[pf-search] fetchAndReplace failed', err);
          window.location.href = url;
        } finally {
          this.removeAttribute('data-loading');
        }
      }

      async fetchSectionHtml(url) {
        const fetchUrl = url + (url.includes('?') ? '&' : '?') + 'section_id=' + encodeURIComponent(this.sectionId);
        const res = await fetch(fetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!res.ok) throw new Error('Section fetch failed: ' + res.status);
        return await res.text();
      }

      replaceFromHtml(html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newSection = doc.querySelector('pf-search');
        if (!newSection) {
          console.warn('[pf-search] response had no pf-search element');
          return;
        }

        // Preserve accordion open/closed state across re-renders.
        const accordionState = {};
        this.querySelectorAll('[data-pf-filter-key]').forEach((el) => {
          accordionState[el.dataset.pfFilterKey] = el.open;
        });

        const panels = [
          '[data-pf-topbar]',
          '[data-pf-sidebar]',
          '[data-pf-main]',
          '[data-pf-drawer]',
        ];
        panels.forEach((sel) => {
          const incoming = newSection.querySelector(sel);
          const current = this.querySelector(sel);
          if (incoming && current) {
            current.replaceWith(incoming);
          }
        });

        this.querySelectorAll('[data-pf-filter-key]').forEach((el) => {
          const key = el.dataset.pfFilterKey;
          if (Object.prototype.hasOwnProperty.call(accordionState, key)) {
            el.open = accordionState[key];
          }
        });

        this.rebindAll();
      }

      rebindAll() {
        this.abortController.abort();
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.bindDrawer(signal);
        this.bindFilterForms(signal);
        this.bindSort(signal);
        this.bindPagination(signal);
        this.bindPriceRanges();
        this.rebindSwatches();
      }

      rebindSwatches() {
        const cards = this.querySelectorAll('[data-pf-grid] .pf-product-card');
        cards.forEach((card) => {
          if (card.dataset.pfSwatchesBound === 'true') return;
          const swatches = card.querySelectorAll('.pf-product-card__swatch');
          if (swatches.length === 0) return;
          const primary = card.querySelector('.pf-product-card__image--primary');
          if (!primary) return;
          card.dataset.pfSwatchesBound = 'true';

          const originalSrc = primary.getAttribute('src');
          const originalSrcset = primary.getAttribute('srcset');

          swatches.forEach((swatch) => {
            swatch.addEventListener('click', (event) => {
              event.preventDefault();
              event.stopPropagation();
              const newSrc = swatch.dataset.imageSrc;
              const newSrcset = swatch.dataset.imageSrcset;
              if (!newSrc) return;
              if (newSrcset) primary.setAttribute('srcset', newSrcset);
              primary.setAttribute('src', newSrc);
              swatches.forEach((s) => s.removeAttribute('aria-pressed'));
              swatch.setAttribute('aria-pressed', 'true');
            });
          });

          card.addEventListener('mouseleave', () => {
            const active = card.querySelector('.pf-product-card__swatch[aria-pressed="true"]');
            if (!active) {
              primary.setAttribute('src', originalSrc);
              if (originalSrcset) primary.setAttribute('srcset', originalSrcset);
            }
          });
        });
      }

      // ---- Price ranges ----
      bindPriceRanges() {
        this.querySelectorAll('[data-pf-price]').forEach((wrap) => this.initPriceRange(wrap));
      }

      initPriceRange(wrap) {
        const minThumb = wrap.querySelector('[data-pf-price-thumb="min"]');
        const maxThumb = wrap.querySelector('[data-pf-price-thumb="max"]');
        const minInput = wrap.querySelector('[data-pf-price-input="min"]');
        const maxInput = wrap.querySelector('[data-pf-price-input="max"]');
        const fill = wrap.querySelector('[data-pf-price-fill]');
        if (!minThumb || !maxThumb || !minInput || !maxInput) return;

        const max = parseFloat(wrap.dataset.max) || 0;
        const min = parseFloat(wrap.dataset.min) || 0;

        const curMin = parseFloat(wrap.dataset.curMin);
        const curMax = parseFloat(wrap.dataset.curMax);
        if (!Number.isNaN(curMin)) minThumb.value = curMin;
        if (!Number.isNaN(curMax)) maxThumb.value = curMax;

        const pct = (val) => {
          if (max === min) return 0;
          return ((val - min) / (max - min)) * 100;
        };

        const updateFill = () => {
          if (!fill || max === min) return;
          const lo = parseFloat(minThumb.value);
          const hi = parseFloat(maxThumb.value);
          const loPct = pct(lo);
          const hiPct = pct(hi);
          fill.style.left = loPct + '%';
          fill.style.width = Math.max(0, hiPct - loPct) + '%';
          fill.style.right = 'auto';
        };

        const writeInputs = (lo, hi) => {
          minInput.value = lo <= min ? '' : String(lo);
          maxInput.value = hi >= max ? '' : String(hi);
        };

        const syncFromThumbs = () => {
          let lo = parseFloat(minThumb.value);
          let hi = parseFloat(maxThumb.value);
          if (lo > hi) {
            if (this._lastTouched === 'max') {
              minThumb.value = hi;
              lo = hi;
            } else {
              maxThumb.value = lo;
              hi = lo;
            }
          }
          writeInputs(lo, hi);
          updateFill();
        };

        const syncFromInputs = () => {
          let lo = parseFloat(minInput.value);
          let hi = parseFloat(maxInput.value);
          if (Number.isNaN(lo)) lo = min;
          if (Number.isNaN(hi)) hi = max;
          lo = Math.max(min, Math.min(lo, max));
          hi = Math.max(min, Math.min(hi, max));
          if (lo > hi) {
            if (this._lastTouched === 'max') lo = hi;
            else hi = lo;
          }
          minThumb.value = lo;
          maxThumb.value = hi;
          writeInputs(lo, hi);
          updateFill();
        };

        minThumb.addEventListener('input', () => { this._lastTouched = 'min'; syncFromThumbs(); });
        maxThumb.addEventListener('input', () => { this._lastTouched = 'max'; syncFromThumbs(); });
        minInput.addEventListener('change', () => { this._lastTouched = 'min'; syncFromInputs(); });
        maxInput.addEventListener('change', () => { this._lastTouched = 'max'; syncFromInputs(); });
        updateFill();
      }
    }

    customElements.define('pf-search', PfSearch);
  }

  // ---------------------------------------------------------------------------
  // Helper: format a price in cents to the current shop's currency format.
  // Uses Shopify.formatMoney if present, otherwise a basic two-decimal format.
  // ---------------------------------------------------------------------------
  function formatMoney(cents) {
    if (cents == null) return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(cents, window.Shopify.currency?.money_format);
      } catch (e) {
        // fall through
      }
    }
    const amount = (cents / 100).toFixed(2);
    return `€${amount}`;
  }
})();
